import React, { useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';

const Hero: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current || !textRef.current) return;
      
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      
      const moveX = (x - 0.5) * 20;
      const moveY = (y - 0.5) * 20;
      
      textRef.current.style.transform = `translate(${moveX}px, ${moveY}px)`;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      id="home" 
      ref={heroRef}
      className="relative h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-black via-black to-purple-950/10"
    >
      <div 
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(110, 47, 251, 0.15), transparent 50%)',
        }}
      ></div>
      
      {/* Animated gradient circle */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-r from-purple-600/10 to-blue-600/10 animate-pulse-slow blur-3xl"></div>
      
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'linear-gradient(0deg, transparent 24%, rgba(110, 47, 251, 0.3) 25%, rgba(110, 47, 251, 0.3) 26%, transparent 27%, transparent 74%, rgba(110, 47, 251, 0.3) 75%, rgba(110, 47, 251, 0.3) 76%, transparent 77%, transparent)',
          backgroundSize: '50px 50px',
        }}
      ></div>

      <div className="relative z-10 flex flex-col items-center justify-center px-4 max-w-4xl gap-4" ref={textRef}>
        <div className="mb-2 inline-block">
          <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-blue-500 blur-lg opacity-70 animate-pulse-slow"></div>
            <div className="absolute inset-1 rounded-full bg-gray-900 flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-blue-900/50"></div>
              <div className="relative w-full h-full flex items-center justify-center">
                {/* Replace with your own AI avatar or icon */}
                <svg width="50%" height="50%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="url(#paint0_linear)" strokeWidth="2"/>
                  <path d="M8 9H16M8 13H14M8 17H12" stroke="url(#paint1_linear)" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 9L12 17" stroke="url(#paint2_linear)" strokeWidth="2" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="paint0_linear" x1="2" y1="12" x2="22" y2="12" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#6E2FEB"/>
                      <stop offset="1" stopColor="#00B3FF"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear" x1="8" y1="13" x2="16" y2="13" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#6E2FEB"/>
                      <stop offset="1" stopColor="#00B3FF"/>
                    </linearGradient>
                    <linearGradient id="paint2_linear" x1="12" y1="13" x2="16" y2="13" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#6E2FEB"/>
                      <stop offset="1" stopColor="#00B3FF"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Removed Open Terminal button from here */}
        
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold font-orbitron mb-4 tracking-tighter">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">S.</span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">SRIVARUN</span>
        </h1>
        
        <p className="text-lg sm:text-xl tracking-wider mb-8 text-gray-300 font-light">
          <span className="inline-block px-4 py-1 rounded-full bg-purple-900/20 border border-purple-800/30 text-purple-300 mb-2 md:mr-2">AI/ML Engineer</span>
          <span className="inline-block px-4 py-1 rounded-full bg-blue-900/20 border border-blue-800/30 text-blue-300 mb-2 md:mx-2">Agentic AI</span>
          <span className="inline-block px-4 py-1 rounded-full bg-indigo-900/20 border border-indigo-800/30 text-indigo-300 mb-2 md:ml-2">Generative AI</span>
        </p>
        
        <div className="relative mt-10">
          <a 
            href="#about"
            className="inline-block group"
          >
            <span className="absolute -inset-0.5 animate-pulse rounded-lg bg-gradient-to-r from-purple-600 to-blue-500 opacity-75 blur transition duration-300 group-hover:opacity-100"></span>
            <span className="relative flex items-center justify-center px-6 py-3 bg-black rounded-lg leading-none">
              <span className="text-purple-300 group-hover:text-white transition duration-300">Explore</span>
              <ArrowDown className="ml-2 h-4 w-4 text-purple-300 group-hover:text-white transition duration-300 animate-bounce" />
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Hero;